/* istanbul ignore file */
import React, { useEffect, useRef, useState } from 'react';
import {
  Sorting as SortingStyled,
  SortingOpener as SortingOpenerStyled,
  SortingModalSelector
} from './Sorting.styles';
import { RelevancyExplanationModalOpener } from './RelevancyExplanationModalOpener';
import { SortingProps } from 'models/SortingProps';
import { defaultSorting } from './SortingValues';
import { FormattedMessage, useIntl } from 'react-intl';
import { buildDropdownItems } from './Sorting.helpers';
import {
  Icon,
  Dropdown,
  useOutsideClick,
  Tooltip,
  Link,
  TooltipHandleFunctions
} from '@trustedshops/tps-seo-shared-frontend-react-components';
import { iconVariables } from 'styles/icon.variables';
import { RelevancyExplanationModalBody } from 'components/ShopResultListHeader/Sorting/RelevancyExplanationModalBody';
import { SORT_MODAL_OPEN_BUTTON } from 'services/data-test-attributes';

const Sorting = ({
  sort,
  setModalVisible,
  activeFilters,
  onParameterChange
}: SortingProps) => {
  const { formatMessage } = useIntl();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownVersion, setDropdownVersion] = useState(false);
  const refForTooltip = useRef<HTMLSpanElement>(null);
  const tooltipComponentRef = useRef<TooltipHandleFunctions>(null);

  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick(ref, () => setDropdownOpen(false));

  useEffect(() => {
    if (typeof window !== undefined) {
      const isMobile = window.matchMedia('(max-width: 1024px)');
      setDropdownVersion(!isMobile.matches);
    }
    return () => {};
  });

  const currentSort = sort || defaultSorting;

  function onClickHandler(event: React.MouseEvent) {
    if (
      /* istanbul ignore next */
      event?.target instanceof HTMLElement &&
      event?.target.closest('.relevancy-explanation-opener')
    ) {
      event.stopPropagation();
      tooltipComponentRef.current?.showDrawer();
    } else {
      if (dropdownVersion) {
        setDropdownOpen(!dropdownOpen);
      } else {
        setModalVisible('sorting');
      }
    }
  }

  const sortingExplanationTooltip = (
    <Tooltip
      childrenRef={refForTooltip}
      content={
        <RelevancyExplanationModalBody>
          <FormattedMessage id="SORTING.SORT_ORDER.EXPLANATION" />{' '}
          <Link
            href={formatMessage({
              id: 'SORTING.SORT_ORDER.READ_MORE.LINK'
            })}
            target="_blank"
          >
            <FormattedMessage id="SORTING.SORT_ORDER.READ_MORE.TEXT" />
          </Link>
        </RelevancyExplanationModalBody>
      }
      placement={'bottom'}
      uniqueId={'sort-selector-info'}
      ref={tooltipComponentRef}
      mobileElementType={'drawer'}
      drawerHeadline={formatMessage({ id: 'MODAL.SORTING.SELECTOR' })}
    >
      <RelevancyExplanationModalOpener
        data-test={SORT_MODAL_OPEN_BUTTON}
        iconName="icon-feedback-info-circle-outline"
        iconSize="20px"
        lineHeight="24px"
        onClick={onClickHandler}
        className="relevancy-explanation-opener"
      />
    </Tooltip>
  );

  const sortingModalSelector = (
    <SortingModalSelector>
      <FormattedMessage id="MODAL.SORTING.SELECTOR" tagName="span" />
      {sortingExplanationTooltip}
    </SortingModalSelector>
  );

  return (
    <SortingStyled>
      {dropdownVersion &&
        currentSort === 'relevancy' &&
        sortingExplanationTooltip}
      <SortingOpenerStyled
        data-test={SORT_MODAL_OPEN_BUTTON}
        ref={ref}
        onClick={onClickHandler}
        className={dropdownOpen ? 'dropdownOpen' : undefined}
      >
        {dropdownVersion
          ? formatMessage({ id: `SORTING.${currentSort.toUpperCase()}` })
          : sortingModalSelector}
        <Icon
          iconName="icon-arrow-chevron-down"
          iconSize={iconVariables.SIZE_L}
          lineHeight={iconVariables.LINE_HEIGHT_REGULAR}
        />
        {dropdownVersion && (
          <Dropdown open={dropdownOpen} className={'sorting-area'}>
            {buildDropdownItems(currentSort, activeFilters, onParameterChange)}
          </Dropdown>
        )}
      </SortingOpenerStyled>
    </SortingStyled>
  );
};
export default Sorting;
